import { ManifoldWindow } from 'build/mount';
import { mixins } from 'vue-class-component';
import { setProviderEventsHandler } from '@manifoldxyz/manifold-dropsitetools-lib';
import { EthereumProvider } from '@manifoldxyz/manifold-sdk';
import RedeemServerClient from '@/api/redeem/redeem';
import ShopifyClient from '@/api/shopify/shopify';
import { ShopifyCart } from '@/api/shopify/ShopifyCart';
import { CAMPAIGN_STORE } from '@/constants/campaign';
import { MCheckoutWidgetInjectContext } from '@/exports/MCheckoutWidgetProps';
import store from '@/store';

export default class CheckoutWidgetMixin extends mixins(MCheckoutWidgetInjectContext) {
  // Important to note - the async calls in here are not resolved
  // before the component is mounted. So state is not always defined.
  async created() {
    const shopifyClient = new ShopifyClient();

    // Get current cart, will be used to initialize the store
    const currentCart = await shopifyClient.getBrowserCart();

    this.mmid = `${currentCart.token}`;
    if (this.clientId) {
      this.$store.commit(`${this.mmid}/setClientId`, this.clientId, { root: true });
    }
    if (this.appName) {
      this.$store.commit(`${this.mmid}/setAppName`, this.appName, { root: true });
    }
    // if (this.grantType) {
    //   this.$store.commit(`${this.mmid}/setGrantType`, this.grantType, { root: true });
    // }

    // SET UP STORE FOR THIS CAMPAIGN
    this.$store = (window as Window as ManifoldWindow)[CAMPAIGN_STORE];
    if (!this.$store.state[this.mmid]) {
      this.$store.registerModule(`${this.mmid}`, store);
      this.$store.commit(`${this.mmid}/setShopifyAPIClient`, shopifyClient, { root: true });
      this.$store.commit(`${this.mmid}/setRedeemAPIClient`, new RedeemServerClient(), {
        root: true
      });
    }

    this.$watch('state.leadingComponent', this.onLeadingComponent);
    this.registerLeadingComponent();

    setProviderEventsHandler(this.initialize);
    window.addEventListener('m-authenticated', this.initialize);

    const provider = EthereumProvider.provider();
    if (!provider) {
      // No provider connected yet, so attempt to initialize connection
      await EthereumProvider.initialize(this.network);
    }

    if (EthereumProvider.selectedAddress()) {
      await this.initialize();
    }
  }

  interval() {
    setTimeout(() => {
      this.$store.dispatch(`${this.mmid}/updateCountdown`, { root: true });
      requestAnimationFrame(this.interval);
    }, 1000);
  }

  onLeadingComponent(value: any, _oldValue: any) {
    if (!value) {
      this.registerLeadingComponent();
    }
  }

  beforeUnmount() {
    if (this.state.leadingComponent === this) {
      this.$store.commit(`${this.mmid}/setLeadingComponentForMMID`, undefined, { root: true });
    }
  }

  registerLeadingComponent() {
    if (!this.state || !this.state.leadingComponent) {
      this.$store.commit(`${this.mmid}/setLeadingComponentForMMID`, this, { root: true });
    }
  }

  async initialize() {
    if (this.state.leadingComponent === this) {
      const handling = this.state.isHandlingProviderEvent;

      const connectedAddress = EthereumProvider.selectedAddress();
      // Process if we are not already handling or the address being handled has changed
      if (!handling.status || handling.address != connectedAddress) {
        this.$store.commit(
          `${this.mmid}/setIsHandlingProviderEvent`,
          { status: true, address: connectedAddress },
          { root: true }
        );
        this.$store.commit(`${this.mmid}/setWrongChain`, !EthereumProvider.chainIsCorrect(), {
          root: true
        });

        try {
          await this.updateState();
        } catch (e) {
          console.error(e);
        }

        this.$store.commit(
          `${this.mmid}/setIsHandlingProviderEvent`,
          { status: false, address: connectedAddress },
          { root: true }
        );
      }
    }
  }

  async updateState(force = false) {
    if (force || this.state.leadingComponent === this) {
      // Start loading
      if (!force) {
        this.$store.commit(`${this.mmid}/setLoaded`, false, { root: true });
      }
      const loading = this.state.loading;
      this.$store.commit(`${this.mmid}/setLoading`, true, { root: true });
      try {
        if (!loading) {
          await this.initializeCart();
        }
      } finally {
        this.$store.commit(`${this.mmid}/setLoaded`, true, { root: true });
        this.$store.commit(`${this.mmid}/setLoading`, false, { root: true });
      }
    }
  }

  /**
   * Initialize the token cart and sync with browser cart
   *
   * @returns Whether or not the wallet address has changed
   */
  async initializeCart(): Promise<boolean> {
    const connectedAddress = EthereumProvider.selectedAddress();
    const walletAddressChanged = this.state.walletAddress !== connectedAddress;

    let oauthToken;
    if (connectedAddress) {
      oauthToken = await this.getOAuth(true);
    }

    let storedShopifyCart = undefined;
    let currentShop;
    // Re-retrieve the token cart from server if we are connected and the wallet address has changed
    if (oauthToken && walletAddressChanged) {
      currentShop = await this.state.ShopifyAPIClient.getCurrentShop();
      // Get cart from Redeem
      storedShopifyCart = await this.state.RedeemAPIClient.getCart(oauthToken, currentShop);
    }

    // Clobber the existing token cart items in the browser cart if the wallet address has changed
    if (walletAddressChanged) {
      const shopifyCart = new ShopifyCart(await this.state.ShopifyAPIClient.getBrowserCart());
      const resp = await shopifyCart.initialSyncWithTokenCart(
        storedShopifyCart,
        this.cartIconSection
      );
      if (resp && resp.items.length === 0 && storedShopifyCart.items.length > 0) {
        // The shopify cart was cleared due to an error
        // so we need to clear out the redeem server cart as well
        storedShopifyCart = await this.state.RedeemAPIClient.clearCart(oauthToken, currentShop);
      }

      if (resp) {
        // If we changed the cart, reload the page
        window.location.reload();
      }

      // Save updated cart only if walletAddressChanged
      this.$store.commit(`${this.mmid}/setStoredShopifyCart`, storedShopifyCart, { root: true });
    }
    this.$store.commit(
      `${this.mmid}/setWalletAddress`,
      !!oauthToken ? connectedAddress : undefined,
      { root: true }
    );
    this.$store.commit(`${this.mmid}/setLoaded`, true, { root: true });
    return walletAddressChanged;
  }

  getOAuth(delayAuth = false) {
    return EthereumProvider.getOAuth({
      grantType: this.state.grantType,
      appName: this.state.appName,
      clientId: this.state.clientId,
      strictAuth: true,
      delayAuth: delayAuth
    });
  }

  get state() {
    return this.$store.state[this.mmid];
  }

  get getters() {
    return {
      walletConnected: this.$store.getters[`${this.mmid}/walletConnected`]
    };
  }
}
